import * as Sentry from '@sentry/react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { useSelector } from 'react-redux';

import { fetchBarryWebsocketConnectionUrl } from '~api/common/barry/barry';
import { GlobalState } from '~reducers';
import { ApiError } from '~types/api';
import { callErrorToast } from '~utils/toasts';

export type BarryWebsocketConnectionUrlResponse = { url: string };

const QUERY_KEY = ['barry-websocket-connection-url'];

export const useBarryWebsocketConnectionUrl = () => {
	const { userId, business } = useSelector((state: GlobalState) => state?.session);
	const queryClient = useQueryClient();

	const query = useQuery<unknown, ApiError, BarryWebsocketConnectionUrlResponse | null>(
		{
			queryKey: QUERY_KEY,
			queryFn: async () => {
				if (!userId || !business?.id) {
					return null;
				}

				const { data } = await fetchBarryWebsocketConnectionUrl({
					userId,
					businessId: business?.id,
				});
				return data;
			},
			onError: (error) => {
				Sentry.captureException(error);
				callErrorToast({
					content:
						'An error occurred while fetching the barry websocket connection URL',
				});
			},
			enabled: !!userId && !!business?.id,
			staleTime: 0,
			cacheTime: 0,
			refetchOnWindowFocus: false,
			refetchOnMount: false,
			refetchOnReconnect: false,
		},
	);

	const fetchFreshUrl = useCallback(async () => {
		queryClient.removeQueries({ queryKey: QUERY_KEY });
		return query.refetch();
	}, []);

	return {
		...query,
		fetchFreshUrl,
	};
};
